import { rpx } from 'client/lib/rpx-client';
import { useMemo, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import dayjs from 'dayjs';

type Member = Awaited<
  ReturnType<typeof rpx.engagementRetention.getMemberSignals>
>['members'][number];

type SortKey = 'newest' | 'longest' | 'mostEngaged' | 'lastSeen';
type FilterKey =
  | 'all'
  | 'paid'
  | 'invited'
  | 'opensEmails'
  | 'neverOpens'
  | 'joinedThisWeek'
  | 'hasActivity';

const SORT_LABELS: Record<SortKey, string> = {
  newest: 'Newest first',
  longest: 'Longest tenure first',
  mostEngaged: 'Most email-engaged',
  lastSeen: 'Last active',
};

const FILTER_LABELS: Record<FilterKey, string> = {
  all: 'All',
  paid: 'Paid',
  invited: 'Invited',
  opensEmails: 'Opens emails',
  neverOpens: 'Never opens',
  joinedThisWeek: 'Joined this week',
  hasActivity: 'Has community activity',
};

export function SegmentDrawer(props: {
  title: string;
  subtitle: string;
  members: Member[];
  actionLabel: string;
  emptyText: string;
  onClose: () => void;
  onOpen: (m: Member) => void;
  onAction: (m: Member) => void;
}) {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortKey>('newest');
  const [filter, setFilter] = useState<FilterKey>('all');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let rows = props.members;
    if (q) {
      rows = rows.filter(
        (m) => (m.name || '').toLowerCase().includes(q) || m.email.toLowerCase().includes(q),
      );
    }
    if (filter === 'paid') {
      rows = rows.filter((m) => m.membershipSource === 'paid');
    } else if (filter === 'invited') {
      rows = rows.filter((m) => m.membershipSource === 'invited');
    } else if (filter === 'opensEmails') {
      rows = rows.filter(
        (m) =>
          m.emailEngagementBucket === 'active_live' || m.emailEngagementBucket === 'async_engaged',
      );
    } else if (filter === 'neverOpens') {
      rows = rows.filter(
        (m) => m.emailEngagementBucket === 'inactive' || m.emailEngagementBucket === 'drifting',
      );
    } else if (filter === 'joinedThisWeek') {
      rows = rows.filter((m) => m.tenureDays <= 7);
    } else if (filter === 'hasActivity') {
      rows = rows.filter((m) => m.postsCount + m.commentsCount > 0);
    }
    rows = [...rows].sort((a, b) => {
      if (sort === 'newest') {
        return a.tenureDays - b.tenureDays;
      }
      if (sort === 'longest') {
        return b.tenureDays - a.tenureDays;
      }
      if (sort === 'mostEngaged') {
        const av = a.opDateLastEmailReceived ? new Date(a.opDateLastEmailReceived).getTime() : 0;
        const bv = b.opDateLastEmailReceived ? new Date(b.opDateLastEmailReceived).getTime() : 0;
        return bv - av;
      }
      if (sort === 'lastSeen') {
        const av = a.lastActiveAt ? new Date(a.lastActiveAt).getTime() : 0;
        const bv = b.lastActiveAt ? new Date(b.lastActiveAt).getTime() : 0;
        return bv - av;
      }
      return 0;
    });
    return rows;
  }, [props.members, query, sort, filter]);

  return createPortal(
    <div class="fixed inset-0 z-[1000] bg-black/30" onClick={props.onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
        onClick={(e) => e.stopPropagation()}
        class="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col border-l border-gray-200 bg-white shadow-xl"
      >
        <header class="flex items-start justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h2 class="text-base font-semibold text-gray-900">
              {props.title}{' '}
              <span class="text-sm font-normal text-gray-500">
                ({filtered.length}
                {filtered.length !== props.members.length ? ` of ${props.members.length}` : ''})
              </span>
            </h2>
            <p class="text-xs text-gray-500">{props.subtitle}</p>
          </div>
          <button
            type="button"
            class="text-sm text-gray-500 hover:text-gray-700"
            onClick={props.onClose}
          >
            Close
          </button>
        </header>
        <div class="border-b border-gray-200 px-5 py-3">
          <div class="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={query}
              onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
              placeholder="Search by name or email…"
              class="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <select
              value={sort}
              onChange={(e) => setSort((e.target as HTMLSelectElement).value as SortKey)}
              class="rounded-md border border-gray-300 px-2.5 py-1.5 text-sm text-gray-700"
            >
              {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => (
                <option key={k} value={k}>
                  {SORT_LABELS[k]}
                </option>
              ))}
            </select>
          </div>
          <div class="mt-2 flex flex-wrap gap-1.5">
            {(Object.keys(FILTER_LABELS) as FilterKey[]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setFilter(k)}
                class={`rounded-full border px-2.5 py-1 text-xs ${
                  filter === k
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {FILTER_LABELS[k]}
              </button>
            ))}
          </div>
        </div>
        <div class="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <p class="p-6 text-sm text-gray-500">
              {props.members.length === 0 ? props.emptyText : 'No members match these filters.'}
            </p>
          ) : (
            <ul class="divide-y divide-gray-100">
              {filtered.map((m) => (
                <SegmentRow
                  key={m.userId}
                  member={m}
                  actionLabel={props.actionLabel}
                  onOpen={props.onOpen}
                  onAction={props.onAction}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function SegmentRow(props: {
  member: Member;
  actionLabel: string;
  onOpen: (m: Member) => void;
  onAction: (m: Member) => void;
}) {
  const m = props.member;
  return (
    <li class="flex items-center gap-3 px-5 py-3 hover:bg-gray-50">
      <div class="min-w-0 flex-1">
        <button
          type="button"
          class="flex min-w-0 items-center gap-1.5 text-left text-sm font-medium text-indigo-700 hover:underline"
          onClick={() => props.onOpen(m)}
        >
          <span class="truncate">{m.name || m.email}</span>
          {m.membershipSource === 'invited' && (
            <span class="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
              Invited
            </span>
          )}
        </button>
        <p class="truncate text-xs text-gray-500">
          <TenureChip tenureDays={m.tenureDays} />
          <CallsChip callsAttended={m.callsAttended} lastCallAttendedAt={m.lastCallAttendedAt} />
          <EmailChip bucket={m.emailEngagementBucket} lastEmail={m.opDateLastEmailReceived} />
          <ActivityChip postsCount={m.postsCount} commentsCount={m.commentsCount} />
        </p>
      </div>
      <button
        type="button"
        class="shrink-0 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
        onClick={() => props.onAction(m)}
      >
        {props.actionLabel}
      </button>
    </li>
  );
}

function TenureChip(props: { tenureDays: number }) {
  const d = props.tenureDays;
  const label =
    d <= 0
      ? 'Today'
      : d < 14
        ? `Joined ${d}d ago`
        : d < 60
          ? `Joined ${Math.round(d / 7)}w ago`
          : `Joined ${Math.round(d / 30)}mo ago`;
  return <span>{label}</span>;
}

function CallsChip(props: { callsAttended: number; lastCallAttendedAt?: Date }) {
  if (props.callsAttended === 0) {
    return <span> · 0 calls</span>;
  }
  const last = props.lastCallAttendedAt ? dayjs(props.lastCallAttendedAt).fromNow() : 'never';
  return (
    <span>
      {' · '}
      {props.callsAttended} calls · last {last}
    </span>
  );
}

function EmailChip(props: { bucket: Member['emailEngagementBucket']; lastEmail?: Date }) {
  if (props.bucket === 'unknown') {
    return undefined;
  }
  const labels: Record<Exclude<Member['emailEngagementBucket'], 'unknown'>, string> = {
    active_live: 'Live engaged',
    async_engaged: 'Recent email',
    drifting: 'Email drift',
    inactive: 'Email inactive',
  };
  const colors: Record<Exclude<Member['emailEngagementBucket'], 'unknown'>, string> = {
    active_live: 'text-emerald-700',
    async_engaged: 'text-emerald-700',
    drifting: 'text-amber-700',
    inactive: 'text-gray-500',
  };
  const label = labels[props.bucket];
  const detail = props.lastEmail ? ` (last ${dayjs(props.lastEmail).fromNow()})` : '';
  return (
    <span class={colors[props.bucket]}>
      {' · '}
      {label}
      {detail}
    </span>
  );
}

function ActivityChip(props: { postsCount: number; commentsCount: number }) {
  const total = props.postsCount + props.commentsCount;
  if (total === 0) {
    return undefined;
  }
  return (
    <span class="text-emerald-700">
      {' · '}
      {props.postsCount} posts · {props.commentsCount} comments
    </span>
  );
}
